// This Modal is for duty assignments of shifts to volunteers and opens on Event Details page

import React, { useEffect, useRef, useState } from 'react'
import { ConfirmationModal, ConflictInformationModal, LoadingSpinner, useAuth, VolunteerFilters, VolunteerProfileModal } from '../components'
import { AllLanguages, ALLOCATED_DEFAULT_FILTERS, ALLOCATION_STATUS_OPTIONS, API_BASE_URL, AVAILABILITY_OPTIONS, ConfirmationModalMeta, ConflictInformationTableMeta, DEFAULT_FILTERS, GenderOptions, IVPositionFilterOptions, LoadingSpinnerMeta, ShiftMoveModalMeta, UPLOAD_DOCUMENT_TYPES } from '../data'
import {
    BaseFilterUpdate,
    Event,
    Flag,
    Shift,
    ShiftAssignmentFormMetaProps,
    ShiftWithDuty,
    Training,
    VolunteerFiltersWithConflictProps,
    VolunteerWithAllocation,
    VolunteerWithConflict,
    Allocation
} from '../data/types'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon, UnlockIcon } from '../icons'
import { ShiftAssignmentVolunteerTable } from './ShiftAssignmentVolunteerTable'
import { Button, Modal } from './ui'
import { ShiftMoveModal } from './ShiftMoveModal'
import { XIcon } from '../icons'
import { useResourcePermissions } from '../hooks/useResourcePermissions'
import { EU_JAMAT_KHANA_OPTIONS } from '../data/meta'
import { formatDateOnly } from '../utils/dates'

const ITEMS_PER_PAGE = 10

type Props = {
    meta: ShiftAssignmentFormMetaProps
    isOpen: boolean
    shift: ShiftWithDuty
    onClose: () => void
    onAssignment: () => Promise<void>
    canManageAllocations: boolean
    readOnly?: boolean
}

type TabKey = "allocated" | "available";

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

export const ShiftAssignmentModal = (props: Props) => {


    const { meta, isOpen, shift, onClose, onAssignment, readOnly = false, canManageAllocations } = props
    const { user } = useAuth();
    const [error, setError] = useState<string | null>(null)
    const [loadingAllocated, setLoadingAllocated] = useState(false)
    const [loadingAvailable, setLoadingAvailable] = useState(false)
    const [performingAction, setPerformingAction] = useState(false)
    const [filterVolunteers, setFilterVolunteers] = useState<VolunteerWithConflict[]>([])
    const [selectedVolunteers, setSelectedVolunteers] = useState<VolunteerWithConflict[]>([])
    const [Flags, setFlags] = useState<Flag[]>([])
    const [Trainings, setTrainings] = useState<Training[]>([])
    const [Events, setEvents] = useState<Event[]>([])
    const [showDuplicationsModal, setShowDuplicationsModal] = useState(false)
    const [selectedVolunteerForDuplication, setSelectedVolunteerForDuplication] = useState<VolunteerWithConflict | null>(null)
    const [showExcludedSections, setShowExcludedSections] = useState(false)
    const [isPublishShiftModalOpen, setIsPublishShiftModalOpen] = useState(false)
    const [selectedShiftState, setSelectedShiftState] = useState<number | null>(null);
    const [isPublishShiftLoading, setIsPublishShiftLoading] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isShiftMoveModalOpen, setIsShiftMoveModalOpen] = useState(false);
    const [isDeallocatingDuplicate, setIsDeallocatingDuplicate] = useState(false);
    const [isDellocateDuplicateShift, setIsDeallocateDuplicateShift] = useState(false);
    const [selectedVolunteerForProfile, setSelectedVolunteerForProfile] = useState<VolunteerWithConflict | null>(null)
    const [volunteersPerPage, setVolunteersPerPage] = useState<number>(ITEMS_PER_PAGE);
    const { canDeallocateDuplicate } = useResourcePermissions();
    const [selectedVolunteerForConflicts, setSelectedVolunteerForConflicts] = useState<VolunteerWithConflict | null>(null);
    const [showConflictModal, setShowConflictModal] = useState(false);



    const [activeTab, setActiveTab] = useState<TabKey>("allocated");

    const [allocatedVolunteers, setAllocatedVolunteers] = useState<VolunteerWithConflict[]>([]);
    const [availableVolunteers, setAvailableVolunteers] = useState<VolunteerWithConflict[]>([]);
    const [selectedAllocation, setSelectedAllocation] = useState<Allocation | null>(null);

    const [allocatedPagination, setAllocatedPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    });
    const [availablePagination, setAvailablePagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    });

    const [loadingPage, setLoadingPage] = useState<number>(1);

    const hasFetched = useRef(false)

    const [allocatedFilters, setAllocatedFilters] = useState<VolunteerFiltersWithConflictProps>(() => ({ ...ALLOCATED_DEFAULT_FILTERS }));
    const [availableFilters, setAvailableFilters] = useState<VolunteerFiltersWithConflictProps>(() => ({ ...DEFAULT_FILTERS }));
    const filters = activeTab === 'allocated' ? allocatedFilters : availableFilters;
    const activeDefaultFilters = activeTab === 'allocated' ? ALLOCATED_DEFAULT_FILTERS : DEFAULT_FILTERS;



    const canApprove = user.permissions?.includes('approve_shifts');


    const canDeallocate = user.role === "admin" || user.role === "hr_admin" // check for role
    const canMoveAcrossAreas = ['admin', 'hr_admin', 'hr_deputy', 'hr_team_member'].includes(user.role)
    const canMoveSelectedVolunteers = !readOnly
        && canManageAllocations
        && activeTab === 'allocated'
        && selectedVolunteers.length > 0
        && (shift?.shift_status === 0 || shift?.shift_status === 2)

    const normalize = (v: any) => ({
        ...v,
        eligible_for_duty: v.eligible_for_duty === 1,
        ismaili: v.ismaili === 1,
        registered_volunteer: v.registered_volunteer === 1,
        multi_faith_family: v.multi_faith_family === 1,
        volunteer_for_visit: v.volunteer_for_visit !== null && v.volunteer_for_visit !== undefined ? Number(v.volunteer_for_visit) : v.volunteer_for_visit
    });

    const buildQueryString = (page: number, tab: TabKey) => {
        const filters = tab === 'allocated' ? allocatedFilters : availableFilters;
        const params = new URLSearchParams()
        //params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('per_page', volunteersPerPage.toString())
        params.append('page', page.toString())

        // Basic search filters
        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        if (filters.phone_number) params.append('phone', filters.phone_number)
        if (filters.id) params.append('id', filters.id)

        // Advanced filters
        if (filters.country) params.append('country', filters.country)
        if (filters.country_of_birth) params.append('country_of_birth', filters.country_of_birth)
        if (filters.family_origin) params.append('family_origin', filters.family_origin)
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana)
        if (filters.iv_position) params.append('iv_position', filters.iv_position)
        if (filters.phone_number) params.append('phone', filters.phone_number)

        // Boolean filters - only send if explicitly set (not null)
        if (filters.ismaili !== null) params.append('ismaili', filters.ismaili.toString())
        if (filters.registered_volunteer !== null) params.append('registered_volunteer', filters.registered_volunteer.toString())
        if (filters.profile_submitted !== null) params.append('profile_submitted', filters.profile_submitted.toString())
        if (filters.approved_volunteer !== null) params.append('approved_volunteer', filters.approved_volunteer.toString())
        if (filters.eligible_for_duty !== null) params.append('eligible_for_duty', filters.eligible_for_duty.toString())
        if (filters.multi_faith_family !== null) params.append('multi_faith_family', filters.multi_faith_family.toString())
        if (filters.has_support_needs !== null) params.append('has_support_needs', filters.has_support_needs.toString())
        if (filters.conflict !== null) params.append('conflict', filters.conflict.toString())

        // Age filters
        if (filters.age) params.append('age', filters.age.toString())
        if (filters.age_start) params.append('age_start', filters.age_start.toString())
        if (filters.age_end) params.append('age_end', filters.age_end.toString())

        // Array filters - send as JSON
        if (filters.genders && filters.genders.length > 0) {
            params.append('genders', JSON.stringify(filters.genders))
        }
        if (filters.service_record && filters.service_record.length > 0) {
            params.append('service_record', JSON.stringify(filters.service_record))
        }
        if (filters.languages && filters.languages.length > 0) {
            params.append('languages', JSON.stringify(filters.languages))
        }

        if (filters.clusters && filters.clusters.length > 0) {
            params.append('clusters', JSON.stringify(filters.clusters));
        }

        if (filters.tags && filters.tags.length > 0) {
            params.append('tags', JSON.stringify(filters.tags.map(t => t.id)))
        }

        if (filters.allocation_status && filters.allocation_status.length > 0) {
            params.append('status', JSON.stringify(filters.allocation_status.map(as => as)));
        }

        if (filters.trainings && filters.trainings.length > 0) {
            params.append('trainings', JSON.stringify(filters.trainings.map(t => t.id)))
            // Add training filter type (defaults to 'at_least_one' if not specified)
            if (filters.training_filter_type) {
                params.append('training_filter_type', filters.training_filter_type)
            }
        }

        if (filters.documents && filters.documents.length > 0) {
            params.append('documents', JSON.stringify(filters.documents))
        }


        if (filters.availability && filters.availability.length > 0) {
            console.log('Adding availability filter:', filters.availability)
            params.append('availability', JSON.stringify(filters.availability))
            if (filters.availability_filter_type) {
                params.append('availability_filter_type', filters.availability_filter_type)
            }
        }

        // String filters
        if (filters.iiuk_registeration) {
            params.append('iiuk_registeration', filters.iiuk_registeration)
        }

        // Volunteer for visit filter
        if (filters.volunteer_for_visit !== null && filters.volunteer_for_visit !== undefined) {
            params.append('volunteer_for_visit', filters.volunteer_for_visit.toString())
        }

        // Event assignment filter
        if (filters.event_assignment_type && filters.event_assignment_events && filters.event_assignment_events.length > 0) {
            params.append('event_assignment_type', filters.event_assignment_type)
            params.append('event_assignment_events', JSON.stringify(filters.event_assignment_events.map(e => e.id)))
        }

        // Core team assignment filter
        if (filters.core_team_assignment_type) {
            params.append('core_team_assignment_type', filters.core_team_assignment_type)
        }

        const queryString = params.toString()
        console.log('ShiftAssignmentModal Query String:', queryString)
        return queryString
    }

    const fetchAllocated = async (page: number) => {
        if (!shift) return;

        setLoadingPage(page);
        setLoadingAllocated(true);
        setError(null);

        try {
            const queryString = buildQueryString(page, 'allocated')
            const response = await fetch(
                `${API_BASE_URL}/allocation/shift/${shift.id}/allocated?${queryString}`,
                { credentials: 'include' }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch allocated volunteers`);
            }

            const result = await response.json();

            const normalized = result.data.map(normalize);
            setAllocatedVolunteers(normalized);
            console.log("Allocated: ", normalized);
            setAllocatedPagination({
                current_page: result.meta.current_page || 1,
                last_page: result.meta.last_page || 1,
                per_page: result.meta.per_page || ITEMS_PER_PAGE,
                total: result.meta.total || 0
            });
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to load allocated volunteers"
            );
        } finally {
            setLoadingAllocated(false);
        }
    };

    const fetchAvailable = async (page: number) => {
        if (!shift || readOnly) return;

        setLoadingPage(page);
        setLoadingAvailable(true);
        setError(null);

        try {
            const queryString = buildQueryString(page, 'available')
            const response = await fetch(
                `${API_BASE_URL}/allocation/shift/${shift.id}/available?${queryString}`,
                { credentials: 'include' }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch available volunteers`);
            }

            const result = await response.json();

            const normalized = result.data.map(normalize);
            setAvailableVolunteers(normalized);
            setAvailablePagination({
                current_page: result.meta.current_page || 1,
                last_page: result.meta.last_page || 1,
                per_page: result.meta.per_page || ITEMS_PER_PAGE,
                total: result.meta.total || 0
            });
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to load available volunteers"
            );
        } finally {
            setLoadingAvailable(false);
        }
    };

    // Fetch volunteer filter data
    useEffect(() => {
        if (!isOpen || !shift) return;
        if (hasFetched.current) return;

        hasFetched.current = true;

        const fetchData = async () => {
            setError(null);

            try {
                // Load flags, trainings, and events in parallel
                const [flagRes, trainingRes, eventRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/flag`, { credentials: 'include' }),
                    fetch(`${API_BASE_URL}/training`, { credentials: 'include' }),
                    fetch(`${API_BASE_URL}/event`, { credentials: 'include' }),
                ]);

                if (!flagRes.ok) throw new Error("Failed to fetch flags");
                if (!trainingRes.ok) throw new Error("Failed to fetch Trainings");
                if (!eventRes.ok) throw new Error("Failed to fetch events");


                const [flags, trainings, eventsResult] = await Promise.all([
                    flagRes.json(),
                    trainingRes.json(),
                    eventRes.json()
                ]);

                setFlags(flags);
                setTrainings(trainings);

                // Handle double-nested arrays for events
                let events = eventsResult.value || eventsResult;
                if (Array.isArray(events) && events.length > 0 && Array.isArray(events[0])) {
                    events = events[0];
                }
                setEvents(events);

                // Load initial page for both tabs
                await Promise.all([
                    fetchAllocated(1),
                    fetchAvailable(1)
                ]);
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load allocation data"
                );
            }
        };

        fetchData();
    }, [isOpen, shift]);


    // Set filtered volunteers directly from backend data (only status filter client-side)
    useEffect(() => {
        if (!isOpen) return;

        const data = activeTab === "allocated" ? allocatedVolunteers : availableVolunteers;

        // Only apply status filter client-side since it depends on allocation_times data
        let filtered = data;

        if (filters?.status !== null && activeTab === 'allocated') {
            filtered = filtered.filter(v => {
                if (filters.status === false && v.allocation?.status !== true)
                    return true

                if (filters.status === true && v.allocation?.status === true)
                    return true

                return false
            });
        }

        setFilterVolunteers(filtered);
    }, [
        activeTab,
        allocatedVolunteers,
        availableVolunteers,
        filters.status,
        isOpen
    ]);

    // Reset selected volunteers when Tabs change
    useEffect(() => {
        setSelectedVolunteers([]);
    }, [activeTab]);

    // Refetch when any filters change
    useEffect(() => {
        if (!isOpen || !shift || !hasFetched.current) return;

        if (activeTab === 'allocated') {
            fetchAllocated(1);
        } else {
            fetchAvailable(1);
        }
    }, [filters, activeTab, volunteersPerPage]);


    // reset values when modal is closed
    useEffect(() => {
        if (!isOpen) {
            setError(null);
            setSelectedVolunteers([]);
            setActiveTab("allocated");
            hasFetched.current = false;
            setAllocatedVolunteers([]);
            setAvailableVolunteers([]);
            setAllocatedFilters({ ...ALLOCATED_DEFAULT_FILTERS });
            setAvailableFilters({ ...DEFAULT_FILTERS });
            setVolunteersPerPage(ITEMS_PER_PAGE);
        }
    }, [isOpen]);


    // set selection of filterd volunteers
    useEffect(() => {
        if (!isOpen) return
        const filterVolunteerIds = new Set(filterVolunteers.map(v => v.csid))
        setSelectedVolunteers(prev => prev.filter(v => filterVolunteerIds.has(v.csid)));
    }, [filterVolunteers, isOpen])


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (!shift)
                throw new Error("No shift data present.");

            if (selectedVolunteers.length === 0)
                throw new Error("Atleast one volunteer should be selected.")

            setPerformingAction(true);
            setError(null);

            let response;

            if (activeTab === 'available') {
                response = await fetch(`${API_BASE_URL}/allocation/shift/${shift.id}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        volunteer: selectedVolunteers.map(v => v.csid),
                        start_time: shift.start_time.slice(0, 5),
                        end_time: shift.end_time.slice(0, 5)
                    })
                });
            }
            else {
                response = await fetch(`${API_BASE_URL}/allocation/shift/${shift.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        volunteer: selectedVolunteers.map(v => v.csid),
                    })
                });
            }

            if (!response.ok) {
                const responseText = await response.text();
                const message = JSON.parse(responseText)['message'];
                throw new Error(message);
            }

            // refresh event details
            onAssignment()

            // Refetch both tabs to get updated data
            await Promise.all([
                fetchAllocated(allocatedPagination.current_page),
                fetchAvailable(availablePagination.current_page)
            ]);

            // Clear selected volunteers
            setSelectedVolunteers([]);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Error while allocating volunteers");
        }
        finally {
            setPerformingAction(false);
        }

    }


    const handleFiltersChange = (update: BaseFilterUpdate) => {
        const updateFilters = (prev: VolunteerFiltersWithConflictProps) => ({
            ...prev,
            ...update
        });

        if (activeTab === 'allocated') {
            setAllocatedFilters(updateFilters);
        } else {
            setAvailableFilters(updateFilters);
        }
    }

    const handlePageChange = (page: number) => {
        const currentPagination = activeTab === 'allocated' ? allocatedPagination : availablePagination
        if (page >= 1 && page <= currentPagination.last_page) {
            if (activeTab === 'allocated') {
                fetchAllocated(page)
            } else {
                fetchAvailable(page)
            }
        }
    }

    const handleDuplicationClick = (volunteer: VolunteerWithConflict) => {
        setSelectedVolunteerForDuplication(volunteer)
        setShowDuplicationsModal(true)
    }

    const handleDuplicationClickClose = () => {
        setSelectedVolunteerForDuplication(null)
        setShowDuplicationsModal(false)
    }

    const handleProfileClick = (volunteer: VolunteerWithConflict) => {
        setSelectedVolunteerForProfile(volunteer)
        setIsProfileModalOpen(true)
    }

    const handleProfileClickClose = () => {
        setSelectedVolunteerForProfile(null)
        setIsProfileModalOpen(false)
    }

    const handleUpdateShiftStatusClick = (status: number) => {
        setSelectedShiftState(status);
        setIsPublishShiftModalOpen(true);
    }

    const handleUpdateShiftStatusClose = () => {
        setSelectedShiftState(null);
        setIsPublishShiftModalOpen(false);
    }

    const handleUpdateShiftStatus = async (status: number) => {

        try {

            setIsPublishShiftLoading(true);

            const response = await fetch(`${API_BASE_URL}/shift/${0}/status/${shift.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    shift_status: status,
                })
            });

            if (!response.ok) throw new Error();

            shift.shift_status = status;

        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update shift status.");
        }
        finally {
            setIsPublishShiftLoading(false);
            setIsPublishShiftModalOpen(false);
        }
    }

    const handleShiftMoveModalClick = () => {
        setIsShiftMoveModalOpen(true);
    }

    const handleShiftMoveModalClickClose = () => {
        setIsShiftMoveModalOpen(false);
    }

    const handleMoveShiftSubmit = async (data: {
        allocation_ids: number[],
        status: boolean[],
        shift_id: number,
        start_time: string,
        end_time: string
    },
        movewithStatus: boolean

    ) => {

        console.log("Data: ", data);
        const response = await fetch(`${API_BASE_URL}/allocation/move?with-status=${movewithStatus ? 1 : 0}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const message = await response.text()
            throw new Error(message)
        }

        setFilterVolunteers(prev => {
            const selectedIds = new Set(selectedVolunteers.map(s => s.csid));
            return prev.filter(v => !selectedIds.has(v.csid));
        });

        onAssignment();

    }

    const handleDuplicateDeallocateClick = (allocation: Allocation) => {
        setIsDeallocateDuplicateShift(true);
        setSelectedAllocation(allocation);
    }

    const handleDuplicateDeallocateClickClose = () => {
        setIsDeallocateDuplicateShift(false);
        setSelectedAllocation(null);
    }

    const handleDuplicateDeallocateClickSubmit = async () => {
        if (!selectedAllocation) {
            console.log("No selected allocation");
            return;
        }

        setIsDeallocatingDuplicate(true);

        try {
            const response = await fetch(`${API_BASE_URL}/allocation/shift/${selectedAllocation.shift_id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    volunteer: [selectedAllocation.volunteer_id],
                })
            });

            if (!response.ok) {
                const message = await response.text();
                throw new Error(message);
            }

            setSelectedVolunteerForDuplication(prev => {
                if (!prev) return null;

                return {
                    ...prev,
                    allocations: prev.allocations.filter(a => a.id !== selectedAllocation.id)
                };
            });

        }
        catch (err) {
            const error = err instanceof Error ? err.message : "Failed to deallocate volunteer.";
            console.log(error);
        }
        finally {
            setIsDeallocatingDuplicate(false);
            handleDuplicateDeallocateClickClose();
        }

    }

    const handleConflictClick = (volunteer: VolunteerWithConflict) => {
        setSelectedVolunteerForConflicts(volunteer);
        setShowConflictModal(true);
    }

    const handleConflictClickClose = () => {
        setSelectedVolunteerForConflicts(null);
        setShowConflictModal(false);
    }

    const handleDataRefresh = (csid: number, newStatus: boolean | null) => {
        // We assume status changes mostly happen in the 'allocated' list
        setAllocatedVolunteers(prev =>
            prev.map(volunteer => {
                if (volunteer.csid === csid) {
                    // Create a deep copy of the volunteer to update nested allocation status
                    return {
                        ...volunteer,
                        allocation: {
                            ...volunteer.allocation,
                            status: newStatus
                        }
                    };
                }
                return volunteer;
            })
        );
    };
    const selectedTabLoading = activeTab === 'allocated' ? loadingAllocated : loadingAvailable;
    const selectedPagination = activeTab === 'allocated' ? allocatedPagination : availablePagination;

    console.log("Clusters: ", filters.clusters);

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={(readOnly ? "View Allocations - " : meta.title + " - ") + `${shift?.title ? shift?.title : shift?.duty_name}`}
                maxWidth="8xl"
                footer={loadingAllocated || loadingAvailable || readOnly ? null :
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={performingAction}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.cancelButton}
                        </Button>
                        <Button
                            type="submit"
                            form="assign-shift-form"
                            disabled={performingAction || shift?.shift_status !== 0}
                            color={activeTab === 'allocated' ? 'danger' : 'primary'}
                        >
                            {performingAction && selectedVolunteers.length > 0 ? activeTab === 'allocated' ?
                                meta.submittingRemoveButton : meta.submittingAllocateButton : activeTab === 'allocated' ?
                                meta.submitRemoveButton : meta.submitAllocateButton}
                        </Button>
                    </div>
                }
            >
                <form id="assign-shift-form" onSubmit={handleSubmit}>
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">

                        {error && (
                            <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        {shift?.criteria && (
                            <div className="mb-10 dark:text-white">
                                <label><strong>Criteria</strong></label>
                                <p>{shift?.criteria}</p>
                            </div>
                        )}

                        {(loadingAllocated && loadingAvailable) ? (
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        ) : (
                            <>
                                <div className='flex justify-between'>

                                    <div className="flex border-b border-gray-300 dark:border-gray-600 mb-4">
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab("allocated")}
                                            className={`px-5 py-2 -mb-px text-sm font-medium border rounded-t-lg transition-colors
                                    ${activeTab === "allocated"
                                                    ? "bg-white dark:bg-primary-600 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 dark:text-gray-100"
                                                    : "bg-gray-100 dark:bg-gray-700 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                                }`}
                                        >
                                            Allocated ({allocatedPagination.total || 0})
                                        </button>

                                        {!readOnly && shift?.shift_status === 0 &&
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab("available")}
                                                className={`px-5 py-2 -mb-px text-sm font-medium border rounded-t-lg transition-colors
                                    ${activeTab === "available"
                                                        ? "bg-white dark:bg-primary-600 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 dark:text-gray-100"
                                                        : "bg-gray-100 dark:bg-gray-700 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                                    }`}
                                            >
                                                Available ({availablePagination.total || 0})
                                            </button>
                                        }
                                    </div>
                                    {!readOnly && canManageAllocations && (
                                        <div className='flex gap-4'>
                                            {canMoveSelectedVolunteers && (
                                                <div>
                                                    <Button type='button' color='primary' variant='outline' size='sm' onClick={() => handleShiftMoveModalClick()}>
                                                        Move Volunteer{selectedVolunteers.length > 1 ? 's' : ''}
                                                    </Button>
                                                </div>
                                            )}

                                            {shift?.shift_status === 0 ?
                                                (<div>
                                                    <Button type='button' color='primary' onClick={() => { handleUpdateShiftStatusClick(1); }}>
                                                        {meta.publishButton}
                                                    </Button>
                                                </div>) :
                                                (canApprove && (
                                                    shift?.shift_status === 1 ? (
                                                        <div className='flex gap-2 h-9'>
                                                            <Button type='button' color='primary' onClick={() => { handleUpdateShiftStatus(2) }}>
                                                                <LockIcon />
                                                                <span>{meta.lockButton}</span>
                                                            </Button>
                                                            <div>
                                                                <Button type='button' color='primary' onClick={() => { handleUpdateShiftStatus(0); }}>
                                                                    <UnlockIcon />
                                                                    <span>{meta.unlockButton}</span>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <Button type='button' color='primary' onClick={() => { handleUpdateShiftStatus(0); }}>
                                                                <UnlockIcon />
                                                                <span>{meta.unlockButton}</span>
                                                            </Button>
                                                        </div>
                                                    )
                                                ))
                                            }
                                        </div>
                                    )}

                                </div>

                                {/* Volunteer Filters */}
                                <VolunteerFilters
                                    key={activeTab}
                                    initialFilters={filters}
                                    defaultFilters={activeDefaultFilters}
                                    onChange={handleFiltersChange}
                                    tagOptions={Flags}
                                    languageOptions={AllLanguages}
                                    serviceRecordOptions={IVPositionFilterOptions}
                                    genderOptions={GenderOptions}
                                    trainingOptions={Trainings}
                                    availabilityOptions={AVAILABILITY_OPTIONS as unknown as string[]}
                                    showConflict={true}
                                    clusterOptions={EU_JAMAT_KHANA_OPTIONS}
                                    showStatus={activeTab === 'allocated'}
                                    showRegisteredVolunteer={true}
                                    eventOptions={Events}
                                    documentOptions={UPLOAD_DOCUMENT_TYPES}
                                    allocationStatusOptions={activeTab === "allocated" ? ALLOCATION_STATUS_OPTIONS : []}
                                />

                                {/* Selected Volunteers */}
                                <div className='dark:text-gray-200 mt-4 mb-4 flex items-center justify-between'>
                                    <span className="text-sm font-medium">
                                        {selectedVolunteers.length > 0 ? (
                                            <span className="text-primary-600 dark:text-primary-400">
                                                {selectedVolunteers.length} volunteer{selectedVolunteers.length !== 1 ? 's' : ''} selected
                                            </span>
                                        ) : (
                                            'No volunteers selected'
                                        )}
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        Showing {filterVolunteers.length} of {selectedPagination.total} volunteers
                                    </span>
                                    <div className="flex items-center">
                                        <label className='text-sm text-gray-600 dark:text-gray-400'>Volunteers per page: </label>
                                        <select
                                            value={volunteersPerPage}
                                            onChange={(e) => setVolunteersPerPage(Number(e.target.value))}
                                            className="ms-2 rounded border border-gray-300 p-1 text-sm focus:border-blue-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={500}>500</option>
                                        </select>
                                    </div>
                                </div>

                                {selectedTabLoading ? (
                                    <div className="flex justify-center py-12 border border-gray-200 dark:border-gray-700 rounded-lg">
                                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                                    </div>
                                ) : (
                                    <>
                                        {/* Volunteer Table */}
                                        <ShiftAssignmentVolunteerTable
                                            meta={{
                                                idColumn: 'ID',
                                                nameColumn: 'Name',
                                                countryColumn: 'Country',
                                                jamatKhanaColumn: 'Jamatkhana',
                                                ServiceRecordColumn: 'IV Postion',
                                                familyOriginColumn: 'Family Origin',
                                                phoneColumn: "Phone Number",
                                                duplicationsColumn: 'Shift count',
                                                statusColumn: 'Status',
                                                noVolunteersMessage: 'No volunteers found',
                                                viewDuplicationsButton: (count: number) => `View (${count})`
                                            }}
                                            data={filterVolunteers}
                                            showDuplications={true}
                                            showConflicts={true}
                                            showStatus={activeTab === 'allocated'}
                                            selected={selectedVolunteers}
                                            onSelectionChange={setSelectedVolunteers}
                                            onDuplicationSelect={handleDuplicationClick}
                                            onConflictSelect={handleConflictClick}
                                            onProfileClick={handleProfileClick}
                                            onDataRefresh={handleDataRefresh}
                                            lead={shift?.lead}
                                            deputy_lead={shift?.deputy_lead}
                                            readOnly={readOnly}
                                            shift={shift}
                                        />
                                    </>
                                )}

                                {/* Pagination */}
                                {selectedPagination.last_page > 1 && (
                                    <div className="flex items-center justify-between mt-4">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                onClick={() => handlePageChange(1)}
                                                disabled={selectedPagination.current_page <= 1}
                                                size="sm"
                                            >
                                                <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                                            </Button>

                                            <Button
                                                type="button"
                                                onClick={() => handlePageChange(selectedPagination.current_page - 1)}
                                                disabled={selectedPagination.current_page <= 1}
                                                size="sm"
                                            >
                                                <ChevronLeftIcon /> Previous
                                            </Button>

                                            {(() => {
                                                const pageNumbers = []
                                                const maxPagesToShow = 5
                                                let startPage = Math.max(1, selectedPagination.current_page - Math.floor(maxPagesToShow / 2))
                                                let endPage = Math.min(selectedPagination.last_page, startPage + maxPagesToShow - 1)

                                                if (endPage - startPage < maxPagesToShow - 1) {
                                                    startPage = Math.max(1, endPage - maxPagesToShow + 1)
                                                }

                                                if (startPage > 1) {
                                                    pageNumbers.push(
                                                        <Button
                                                            key={1}
                                                            type="button"
                                                            onClick={() => handlePageChange(1)}
                                                            size="sm"
                                                            variant="icon"
                                                        >
                                                            1
                                                        </Button>
                                                    )
                                                    if (startPage > 2) {
                                                        pageNumbers.push(
                                                            <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                                                        )
                                                    }
                                                }

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pageNumbers.push(
                                                        <Button
                                                            key={i}
                                                            type="button"
                                                            onClick={() => handlePageChange(i)}
                                                            size="sm"
                                                            variant={i === loadingPage ? "solid" : "icon"}
                                                            color={i === loadingPage ? "primary" : "neutral"}
                                                        >
                                                            {i}
                                                        </Button>
                                                    )
                                                }

                                                if (endPage < selectedPagination.last_page) {
                                                    if (endPage < selectedPagination.last_page - 1) {
                                                        pageNumbers.push(
                                                            <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                                                        )
                                                    }
                                                    pageNumbers.push(
                                                        <Button
                                                            key={selectedPagination.last_page}
                                                            type="button"
                                                            onClick={() => handlePageChange(selectedPagination.last_page)}
                                                            size="sm"
                                                            variant="icon"
                                                        >
                                                            {selectedPagination.last_page}
                                                        </Button>
                                                    )
                                                }

                                                return pageNumbers
                                            })()}

                                            <Button
                                                type="button"
                                                onClick={() => handlePageChange(selectedPagination.current_page + 1)}
                                                disabled={selectedPagination.current_page >= selectedPagination.last_page}
                                                size="sm"
                                            >
                                                Next <ChevronRightIcon />
                                            </Button>

                                            <Button
                                                type="button"
                                                onClick={() => handlePageChange(selectedPagination.last_page)}
                                                disabled={selectedPagination.current_page >= selectedPagination.last_page}
                                                size="sm"
                                            >
                                                Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                                            </Button>
                                        </div>

                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            Page {selectedPagination.current_page} of {selectedPagination.last_page}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </form>
            </Modal >

            {/* Duplications Modal */}
            {showDuplicationsModal && selectedVolunteerForDuplication && (
                <Modal
                    isOpen={showDuplicationsModal}
                    onClose={() => {
                        setShowDuplicationsModal(false)
                        setSelectedVolunteerForDuplication(null)
                    }}
                    title={meta.duplicationsModalTitle(selectedVolunteerForDuplication.first_name, selectedVolunteerForDuplication.last_name)}
                    maxWidth="5xl"
                    footer={
                        <div className="flex justify-end">
                            <Button
                                onClick={() => {
                                    setShowDuplicationsModal(false)
                                    setSelectedVolunteerForDuplication(null)
                                }}
                                variant="outline"
                            >
                                {meta.closeModalButton}
                            </Button>
                        </div>
                    }
                >
                    <div className="px-6 py-4">
                        {/* Shift Allocations - Split into counted and excluded */}
                        {selectedVolunteerForDuplication.allocations && selectedVolunteerForDuplication.allocations.length > 0 && (() => {
                            const countedAllocations = selectedVolunteerForDuplication.allocations.filter(
                                (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks !== false
                            )
                            const excludedAllocations = selectedVolunteerForDuplication.allocations.filter(
                                (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks === false
                            )

                            const renderAllocationTable = (allocations: any[], isExcluded: boolean = false) => (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className={isExcluded ? "bg-gray-100 dark:bg-gray-700" : "bg-gray-50 dark:bg-gray-800"}>
                                            <tr>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.dateTimeColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                                {canDeallocateDuplicate() && <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.actionsColumn}</th>}
                                            </tr>
                                        </thead>
                                        <tbody className={`${isExcluded ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"} divide-y divide-gray-200 dark:divide-gray-700`}>
                                            {allocations.map((allocation: any, idx: number) => {
                                                const shift = allocation.shift
                                                const eventDate = shift?.area?.event?.start_date || shift?.area?.event?.date
                                                const startTime = shift?.start_time || 'N/A'
                                                const endTime = shift?.end_time || 'N/A'

                                                let statusText = meta.pendingStatus
                                                let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                                if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                    const allocationTime = allocation.allocation_times[0]
                                                    if (allocationTime.status === null) {
                                                        statusText = meta.pendingStatus
                                                        statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                    } else if (allocationTime.status === true || allocationTime.status === 1) {
                                                        statusText = meta.acceptedStatus
                                                        statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                    } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                        statusText = meta.declinedStatus
                                                        statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                                                    }
                                                }

                                                return (
                                                    <tr key={idx} className={isExcluded ? "opacity-75" : ""}>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.area?.event?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.area?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.title || shift?.duty?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {formatDateOnly(eventDate)} {startTime} - {endTime}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                                {statusText}
                                                            </span>
                                                        </td>
                                                        {canDeallocateDuplicate() && <td className="px-4 py-3 text-sm">
                                                            <Button
                                                                type='button'
                                                                color="danger"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleDuplicateDeallocateClick(allocation)}
                                                            >
                                                                <XIcon className="w-4 h-4 me-1" />
                                                                Deallocate
                                                            </Button>
                                                        </td>}
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )

                            return (
                                <>
                                    {/* Counted Allocations */}
                                    {countedAllocations.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                                {meta.shiftAllocationsTitle(countedAllocations.length)}
                                            </h3>
                                            {renderAllocationTable(countedAllocations)}
                                        </div>
                                    )}

                                    {/* Store excluded allocations count for later rendering */}
                                    {excludedAllocations.length > 0 && (
                                        <input type="hidden" data-excluded-allocations={JSON.stringify(excludedAllocations)} />
                                    )}
                                </>
                            )
                        })()}

                        {/* Core Team Assignments */}
                        {selectedVolunteerForDuplication.coreTeams && selectedVolunteerForDuplication.coreTeams.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                    {meta.coreTeamAssignmentsTitle(selectedVolunteerForDuplication.coreTeams.length)}
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.streamsColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.roleColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {selectedVolunteerForDuplication.coreTeams.map((coreTeam: any, idx: number) => {
                                                const statusText = coreTeam.approved ? meta.approvedStatus : meta.submittedStatus
                                                const statusClass = coreTeam.approved
                                                    ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                    : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                                return (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {Array.isArray(coreTeam.streams) ? coreTeam.streams.join(', ') : coreTeam.streams || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {coreTeam.role || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                                {statusText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Lead/Deputy Lead Positions - Split into counted and excluded */}
                        {(() => {
                            // Filter event leads
                            const countedEventLeads = selectedVolunteerForDuplication.eventLeads?.filter(
                                (event: any) => event.include_in_duplicate_checks !== false
                            ) || []
                            const excludedEventLeads = selectedVolunteerForDuplication.eventLeads?.filter(
                                (event: any) => event.include_in_duplicate_checks === false
                            ) || []

                            // Filter areas as lead
                            const countedAreasAsLead = selectedVolunteerForDuplication.areasAsLead?.filter(
                                (area: any) => area.event?.include_in_duplicate_checks !== false
                            ) || []
                            const excludedAreasAsLead = selectedVolunteerForDuplication.areasAsLead?.filter(
                                (area: any) => area.event?.include_in_duplicate_checks === false
                            ) || []

                            // Filter areas as deputy lead
                            const countedAreasAsDeputyLead = selectedVolunteerForDuplication.areasAsDeputyLead?.filter(
                                (area: any) => area.event?.include_in_duplicate_checks !== false
                            ) || []
                            const excludedAreasAsDeputyLead = selectedVolunteerForDuplication.areasAsDeputyLead?.filter(
                                (area: any) => area.event?.include_in_duplicate_checks === false
                            ) || []

                            // Filter shifts as lead
                            const countedShiftsAsLead = selectedVolunteerForDuplication.shiftsAsLead?.filter(
                                (shift: any) => shift.area?.event?.include_in_duplicate_checks !== false
                            ) || []
                            const excludedShiftsAsLead = selectedVolunteerForDuplication.shiftsAsLead?.filter(
                                (shift: any) => shift.area?.event?.include_in_duplicate_checks === false
                            ) || []

                            // Filter shifts as deputy lead
                            const countedShiftsAsDeputyLead = selectedVolunteerForDuplication.shiftsAsDeputyLead?.filter(
                                (shift: any) => shift.area?.event?.include_in_duplicate_checks !== false
                            ) || []
                            const excludedShiftsAsDeputyLead = selectedVolunteerForDuplication.shiftsAsDeputyLead?.filter(
                                (shift: any) => shift.area?.event?.include_in_duplicate_checks === false
                            ) || []

                            const countedTotal = countedEventLeads.length + countedAreasAsLead.length + countedAreasAsDeputyLead.length + countedShiftsAsLead.length + countedShiftsAsDeputyLead.length
                            const excludedTotal = excludedEventLeads.length + excludedAreasAsLead.length + excludedAreasAsDeputyLead.length + excludedShiftsAsLead.length + excludedShiftsAsDeputyLead.length

                            // Get excluded allocations for rendering at the end
                            const excludedAllocations = selectedVolunteerForDuplication.allocations?.filter(
                                (alloc: any) => alloc.shift?.area?.event?.include_in_duplicate_checks === false
                            ) || []

                            const renderLeadPositionsTable = (
                                eventLeads: any[],
                                areasAsLead: any[],
                                areasAsDeputyLead: any[],
                                shiftsAsLead: any[],
                                shiftsAsDeputyLead: any[],
                                isExcluded: boolean = false
                            ) => (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className={isExcluded ? "bg-gray-100 dark:bg-gray-700" : "bg-gray-50 dark:bg-gray-800"}>
                                            <tr>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.positionTypeColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.roleColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`${isExcluded ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"} divide-y divide-gray-200 dark:divide-gray-700`}>
                                            {eventLeads.map((event: any, idx: number) => (
                                                <tr key={`event-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Event</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.pivot?.role || 'Lead'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                </tr>
                                            ))}
                                            {areasAsLead.map((area: any, idx: number) => (
                                                <tr key={`area-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                </tr>
                                            ))}
                                            {areasAsDeputyLead.map((area: any, idx: number) => (
                                                <tr key={`area-deputy-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                </tr>
                                            ))}
                                            {shiftsAsLead.map((shift: any, idx: number) => (
                                                <tr key={`shift-lead-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.title || shift.duty?.name || 'N/A'}</td>
                                                </tr>
                                            ))}
                                            {shiftsAsDeputyLead.map((shift: any, idx: number) => (
                                                <tr key={`shift-deputy-${idx}`} className={isExcluded ? "opacity-75" : ""}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.title || shift.duty?.name || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )

                            // Helper to render excluded allocations table
                            const renderExcludedAllocationsTable = () => (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-100 dark:bg-gray-700">
                                            <tr>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.eventColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.areaColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.shiftColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.dateTimeColumn}</th>
                                                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.statusColumn}</th>
                                                {canDeallocateDuplicate() && <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{meta.actionsColumn}</th>}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-gray-50 dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                                            {excludedAllocations.map((allocation: any, idx: number) => {
                                                const shift = allocation.shift
                                                const eventDate = shift?.area?.event?.start_date || shift?.area?.event?.date
                                                const startTime = shift?.start_time || 'N/A'
                                                const endTime = shift?.end_time || 'N/A'

                                                let statusText = meta.pendingStatus
                                                let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                                if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                    const allocationTime = allocation.allocation_times[0]
                                                    if (allocationTime.status === null) {
                                                        statusText = meta.pendingStatus
                                                        statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                    } else if (allocationTime.status === true || allocationTime.status === 1) {
                                                        statusText = meta.acceptedStatus
                                                        statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                    } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                        statusText = meta.declinedStatus
                                                        statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                                                    }
                                                }

                                                return (
                                                    <tr key={idx} className="opacity-75">
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.area?.event?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.area?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {shift?.title || shift?.duty?.name || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                            {formatDateOnly(eventDate)} {startTime} - {endTime}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                                {statusText}
                                                            </span>
                                                        </td>
                                                        {canDeallocateDuplicate() && <td className="px-4 py-3 text-sm">
                                                            <Button
                                                                type='button'
                                                                color="danger"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleDuplicateDeallocateClick(allocation)}
                                                            >
                                                                <XIcon className="w-4 h-4 me-1" />
                                                                Deallocate
                                                            </Button>
                                                        </td>}

                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )

                            return (
                                <>
                                    {/* Counted Lead Positions */}
                                    {countedTotal > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                                {meta.leadPositionsTitle(countedTotal)}
                                            </h3>
                                            {renderLeadPositionsTable(countedEventLeads, countedAreasAsLead, countedAreasAsDeputyLead, countedShiftsAsLead, countedShiftsAsDeputyLead)}
                                        </div>
                                    )}

                                    {/* Excluded Sections (Collapsible) */}
                                    {(excludedAllocations.length > 0 || excludedTotal > 0) && (
                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setShowExcludedSections(!showExcludedSections)}
                                                className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-3"
                                            >
                                                <ChevronDownIcon className={`w-5 h-5 transition-transform ${showExcludedSections ? 'rotate-0' : '-rotate-90'}`} />
                                                <span className="text-sm font-medium">
                                                    Show Excluded Items ({excludedAllocations.length + excludedTotal})
                                                </span>
                                            </button>

                                            {showExcludedSections && (
                                                <>
                                                    {/* Excluded Allocations */}
                                                    {excludedAllocations.length > 0 && (
                                                        <div className="mb-6">
                                                            <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                                {meta.excludedAllocationsTitle(excludedAllocations.length)}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                                                {meta.excludedAllocationsDescription}
                                                            </p>
                                                            {renderExcludedAllocationsTable()}
                                                        </div>
                                                    )}

                                                    {/* Excluded Lead Positions */}
                                                    {excludedTotal > 0 && (
                                                        <div className="mb-6">
                                                            <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                                {meta.excludedLeadPositionsTitle(excludedTotal)}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                                                {meta.excludedLeadPositionsDescription}
                                                            </p>
                                                            {renderLeadPositionsTable(excludedEventLeads, excludedAreasAsLead, excludedAreasAsDeputyLead, excludedShiftsAsLead, excludedShiftsAsDeputyLead, true)}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )
                        })()}

                        {/* No duplications message */}
                        {(!selectedVolunteerForDuplication.allocations || selectedVolunteerForDuplication.allocations.length === 0) &&
                            (!selectedVolunteerForDuplication.coreTeams || selectedVolunteerForDuplication.coreTeams.length === 0) &&
                            (!selectedVolunteerForDuplication.eventLeads || selectedVolunteerForDuplication.eventLeads.length === 0) &&
                            (!selectedVolunteerForDuplication.areasAsLead || selectedVolunteerForDuplication.areasAsLead.length === 0) &&
                            (!selectedVolunteerForDuplication.areasAsDeputyLead || selectedVolunteerForDuplication.areasAsDeputyLead.length === 0) &&
                            (!selectedVolunteerForDuplication.shiftsAsLead || selectedVolunteerForDuplication.shiftsAsLead.length === 0) &&
                            (!selectedVolunteerForDuplication.shiftsAsDeputyLead || selectedVolunteerForDuplication.shiftsAsDeputyLead.length === 0) && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {meta.noDuplicationsMessage}
                                </p>
                            )}
                    </div>
                </Modal>
            )}

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isPublishShiftModalOpen}
                onConfirm={() => handleUpdateShiftStatus(selectedShiftState)}
                onCancel={handleUpdateShiftStatusClose}
                title={meta.publishShiftTitle}
                message={meta.publishShiftMessage}
                isLoading={isPublishShiftLoading}
                confirmButtonText='Submit'
                loadingButtonText='Submitting..'
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDellocateDuplicateShift}
                onCancel={handleDuplicateDeallocateClickClose}
                onConfirm={handleDuplicateDeallocateClickSubmit}
                title={"Deallocate from Shift"}
                isLoading={isDeallocatingDuplicate}
                message={"This action will deallocate the volunteer from the shift. This action cannot be undone."}
                confirmButtonText='Deallocate'
                loadingButtonText='Deallocating...'
            />

            <VolunteerProfileModal
                volunteerId={selectedVolunteerForProfile?.csid || null}
                isOpen={isProfileModalOpen}
                onClose={handleProfileClickClose}
            />

            <ShiftMoveModal
                meta={ShiftMoveModalMeta}
                isOpen={isShiftMoveModalOpen}
                volunteers={selectedVolunteers as VolunteerWithAllocation[]}
                currentShift={shift}
                sameAreaOnly={!canMoveAcrossAreas}
                onClose={handleShiftMoveModalClickClose}
                onSubmit={handleMoveShiftSubmit}
            />

            <ConflictInformationModal
                meta={ConflictInformationTableMeta}
                conflicts={selectedVolunteerForConflicts?.conflicts}
                isOpen={showConflictModal}
                onCancel={handleConflictClickClose}
                title={meta.conflictTitle}
                message={meta.conflictMessage}
            />
        </>
    )
}
